//core
import React, {useEffect, useState, useCallback} from "react";
import arePropsEqual from "react-fast-compare";
import {useTranslation} from "react-i18next";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {DesktopDatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {TextField, FormControl, Box, InputAdornment, IconButton} from "@mui/material";
import ruLocale from "date-fns/locale/ru";
import ukLocale from "date-fns/locale/uk";
import enLocale from "date-fns/locale/en-US";
import esLocale from "date-fns/locale/es";

//styles
import { useStyles } from "./styles/styles";
import Calendar from "@mui/icons-material/Event";

//types
interface IMuiFieldProps {
  id?: string;
  name: string;
  autoComplete?: string;
  label: string;
  value?: any;
  secondValue?: any;
  maxDate?: any;
  minDate?: any;
  hideError?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  paperClass?: string;
  helperText?: string;
  dataId?: string;
  InputProps?: any;
  error?: boolean | string;
  validation?: (value: any) => any;
  onChange: (value: Date) => any;
  noMB?: boolean;
  extraSmall?: boolean;
  onCloseCallback?: () => void;
  views?: any;
  inputFormat?: string;
  disablePortal?: boolean;
}

const localeMap = {
  en: enLocale,
  ru: ruLocale,
  uk: ukLocale,
  es: esLocale,
};

const CSingleDatePicker = (props: IMuiFieldProps) => {
  const { i18n } = useTranslation();
  const styles = useStyles();

  const {
    id,
    name,
    autoComplete,
    label,
    value,
    secondValue,
    disabled = false,
    maxDate,
    minDate,
    error = '',
    hideError = false,
    className = '',
    paperClass = '',
    noMB = false,
    extraSmall = false,
    inputFormat = 'dd/MM/yyyy',
    onChange,
    onCloseCallback,
    views,
    disablePortal = true,
  } = props;

  const [open, setOpen] = useState(false);
  const [locale, setLocale] = useState<object>(localeMap.en);

  useEffect(() => {
    if (i18n.language === 'ru') {
      setLocale(localeMap.ru);
    }

    if (i18n.language === 'en') {
      setLocale(localeMap.en);
    }

    if (i18n.language === 'uk') {
      setLocale(localeMap.uk);
    }

    if (i18n.language === 'es') {
      setLocale(localeMap.es);
    }
  }, [i18n.language]);

  const handleChange = useCallback(
    (newValue: Date | null) => {
      if (newValue !== null) {
        onChange(newValue);
      }
    },
    [onChange],
  );

  const TextField = React.forwardRef(
    (props: any, ref: React.Ref<HTMLDivElement>) => (
      <TextField
        id={id}
        name={name}
        className={`${styles.input} ${extraSmall && styles.small}`}
        autoComplete={autoComplete}
        size="small"
        InputProps={{
          readOnly: true,
          disabled: true,
        }}
        sx={{ pointerEvents: 'none' }}
        {...props}
        {...(hideError ? {error: false} : {})}
      />
    ),
  );

  return (
    <LocalizationProvider
      //@ts-ignore
      dateAdapter={AdapterDateFns} locale={locale}>
      <Box className={styles.formField} sx={{ marginBottom: noMB ? '0' : '24px' }}>
        <FormControl onClick={() => setOpen(true)} fullWidth>
          <DesktopDatePicker
            className={className}
            open={disabled ? false : open}
            onOpen={() => setOpen(true)}
            onClose={() => {
              setOpen(false);
              if (typeof onCloseCallback === 'function') {
                onCloseCallback();
              }
            }}
            label={label}
            disabled={disabled}
            value={new Date(value)}
            format={inputFormat}
            {...(maxDate ? {maxDate: new Date(maxDate)} : {})}
            {...(minDate ? {minDate: new Date(minDate)} : {})}
            {...(views ? {views: views} : {})}
            // @ts-ignore
            onChange={handleChange}
            slotProps={{
              popper: {
                disablePortal: disablePortal
              },
              layout: {
                sx: {
                  paperClass
                },
              },
              textField: {
                size: 'small',
                inputProps: {
                  readOnly: true,
                  disabled: true,
                  style: {
                    pointerEvents: 'none',
                    userSelect: 'none',
                  },
                },
                InputProps: {
                  endAdornment: <InputAdornment position="end">
                    <IconButton size="small" className={styles.iconButton} sx={{ padding: '2px' }}>
                      <Calendar color="action" />
                    </IconButton>
                  </InputAdornment>,
                  readOnly: true,
                  sx: {
                    userSelect: 'none',
                    cursor: 'pointer',
                  },
                },
              },
              actionBar: ({ wrapperVariant }) => {
                return {
                  actions: wrapperVariant === 'desktop' ? [] : ['accept'],
                }
              },
            }}
          />
          {/*{ meta.error && meta.error !== 'error' ? (
            <FormHelperText error>{meta.error}</FormHelperText>
          ) : null }*/}
        </FormControl>
      </Box>
    </LocalizationProvider>
  );
};

export default React.memo(CSingleDatePicker, arePropsEqual);
